import { z } from 'zod';
import { CHILLDOC_MAX_BYTES } from './chilldoc.js';
import { DocumentKind, DocumentStatus } from './document.js';
import { DocumentShareAudience, ShareSnapshotPresign } from './document-share.js';
/**
 * Chia sẻ **tài liệu dự án** ra ngoài, chỉ xem — APM-PDSH, docs/02-sprint-plan.md "Chia sẻ tài
 * liệu dự án ra ngoài". Người dùng chốt 2026-10-14: **một** thiết lập mỗi tài liệu (một link),
 * ba mức đối tượng như tài liệu cá nhân (`DocumentShareAudience`), bốn cách chọn phiên bản.
 */
/**
 * - `version`: đúng một bản (`versionNo`).
 * - `latest_approved`: bản đã duyệt gần nhất — chưa bản nào duyệt ⇒ người xem thấy "chưa có bản".
 * - `latest`: bản mới nhất, kể cả nháp.
 * - `all`: mọi bản; trang xem có "Phiên bản & duyệt".
 */
export const PROJECT_DOCUMENT_SHARE_VERSION_MODES = [
    'version',
    'latest_approved',
    'latest',
    'all',
];
export const ProjectDocumentShareVersionMode = z.enum(PROJECT_DOCUMENT_SHARE_VERSION_MODES);
export const SetProjectDocumentShareInput = z
    .object({
    audience: DocumentShareAudience,
    versionMode: ProjectDocumentShareVersionMode,
    /** Bắt buộc khi `versionMode = version`, bỏ qua ở ba chế độ còn lại. */
    versionNo: z.number().int().positive().optional(),
})
    .refine((input) => input.versionMode !== 'version' || input.versionNo !== undefined, {
    message: 'Chọn phiên bản để chia sẻ.',
    path: ['versionNo'],
});
/** Một bản `.chilldoc` trong phạm vi chia sẻ mà chưa có bản sạch khớp — trình duyệt dựng rồi tải lên. */
export const PendingShareCopy = z.object({
    versionNo: z.number().int().positive(),
    /** Tệp của bản ấy — đọc nó, bỏ bình luận, tải lên. */
    fileId: z.uuid(),
});
export const ProjectDocumentShare = z.object({
    /** Link đầy đủ — chép lại được (token mã hoá ở server, như link mời). */
    url: z.string(),
    audience: DocumentShareAudience,
    versionMode: ProjectDocumentShareVersionMode,
    /** Chỉ với `versionMode = version`. */
    versionNo: z.number().int().positive().nullable(),
    /** Lượt mở — dấu vết đọc, không phải số người. */
    openCount: z.number().int().nonnegative(),
    lastOpenedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
    /** Bản `.chilldoc` người nhận chưa xem được vì chưa có bản sạch. */
    pendingCopies: z.array(PendingShareCopy),
});
/** `share: null` = chưa chia sẻ — bọc lại vì Nest trả `null` trần thành body rỗng. */
export const ProjectDocumentShareState = z.object({
    share: ProjectDocumentShare.nullable(),
    /** Người gọi đặt / đổi / tắt được không (`document:approve` trong dự án). */
    canManage: z.boolean(),
});
export const ShareCopyPresignInput = z.object({
    sizeBytes: z.number().int().positive().max(CHILLDOC_MAX_BYTES),
});
export const ShareCopyPresign = ShareSnapshotPresign;
export const PutShareCopyInput = z.object({
    key: z.string().trim().min(1).max(512),
    /** Tệp mà bản sạch dựng từ — phải đúng là tệp hiện tại của phiên bản. */
    sourceFileId: z.uuid(),
});
// ---------------------------------------------------------------------------
// Trang xem của người nhận
// ---------------------------------------------------------------------------
/**
 * Trạng thái một bản trong mắt người ngoài: `approved` = bản đã duyệt gần nhất; bản mới nhất mang
 * trạng thái của tài liệu (`draft` / `review`); còn lại là `old`. Không tên người, không ghi chú
 * duyệt (người dùng chốt 2026-10-14).
 */
export const PublicDocumentVersionState = z.enum(['approved', 'review', 'draft', 'old']);
export const PublicDocumentVersion = z.object({
    versionNo: z.number().int().positive(),
    state: PublicDocumentVersionState,
    changeNote: z.string().nullable(),
    createdAt: z.iso.datetime(),
    filename: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
});
/**
 * Nội dung của bản đang xem: tệp (URL đã ký — luật #3), chữ gõ tay, hoặc chưa xem được.
 * `pending` = `.chilldoc` chưa có bản sạch: **không** rơi về tệp gốc (tệp gốc mang bình luận).
 */
export const PublicDocumentBody = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('file'),
        filename: z.string(),
        sizeBytes: z.number().int().nonnegative().nullable(),
        contentUrl: z.string(),
    }),
    z.object({ kind: z.literal('markdown'), content: z.string() }),
    z.object({ kind: z.literal('pending') }),
    z.object({ kind: z.literal('empty') }),
]);
export const PublicProjectDocument = z.object({
    title: z.string(),
    kind: DocumentKind,
    status: DocumentStatus,
    projectName: z.string(),
    orgName: z.string(),
    audience: DocumentShareAudience,
    versionMode: ProjectDocumentShareVersionMode,
    /** Bản đang xem — `null` khi `latest_approved` mà chưa bản nào được duyệt. */
    versionNo: z.number().int().positive().nullable(),
    body: PublicDocumentBody,
    /** Chỉ có với `versionMode = all`; mới nhất trước. */
    versions: z.array(PublicDocumentVersion).nullable(),
    /** Người xem đang đăng nhập (không phải phiên demo). */
    signedIn: z.boolean(),
});
//# sourceMappingURL=project-document-share.js.map